/**
 * Binary Mission sub-KPI type.
 *
 * Discrete pass/fail outcome at a deadline. Score is 0 (not achieved) or
 * 100 (achieved). When deadline-proximity degradation is enabled (default
 * true), unachieved outcomes degrade as the deadline approaches:
 *   - Plenty of time remaining     → 100  (Green — on track)
 *   - Within warning_days          →  80  (Yellow — deadline approaching)
 *   - Within critical_days         →  70  (Red-ish — urgent)
 *   - Overdue                      →   0  (hard fail)
 *
 * Examples: legislation passed, certification obtained, school operational,
 * MOU signed, regulatory approval received.
 *
 * Per Skip's decision: deadline degradation is configurable per-instance
 * via `deadline_degradation_enabled` (default true). Project owners can
 * disable it for outcomes where lateness isn't worse than not-yet-achieved.
 *
 * See: DEV-814 KPI Engine Refinement, Phase 2.
 */

import type {
  ConfigFieldSchema,
  MissionDataPoint,
  MissionSubKpiType,
  SetupExample,
  SubKpiScore,
  UiHints,
  // DEV-963: missing import surfaced after TS 5.4 upgrade enabled real
  // type-checking. ValidationError is defined in ./types and used by
  // validateConfig below; was implicitly available before because TS
  // couldn't parse and type-check this file under the old version.
  ValidationError,
} from './types';
import { latestAsOf } from '../timeSeries';
import { applyDeadlineDegradation } from './deadline';
import { KPI_DEFAULTS } from '../defaults';
import { validateCommonCrossFields } from './commonValidations';

/**
 * Configuration for a Binary sub-KPI instance.
 *
 * Data points carry value 0 (not achieved) or value 1 (achieved). Latest
 * non-null observation determines achievement state.
 */
export interface BinaryConfig extends Record<string, unknown> {
  /** Display name for the metric (e.g., "Legislation Passed"). */
  display_name: string;
  /**
   * Color mode. Binary only supports 'deadline' (its single declared
   * supported_color_modes), but the field is carried explicitly because
   * validateCommonCrossFields is mode-aware and defaults to 'score' when
   * the field is absent — which would silently skip Binary's deadline-
   * window validation. Wizard always sets this; tests must too.
   */
  color_mode?: 'deadline';
  /** Description of what "achieved" means. */
  achievement_definition?: string;
  /** Optional deadline (YYYY-MM-DD) by which achievement is expected. */
  deadline_date?: string;
  /** Days before deadline to trigger Yellow warning. Default 30. */
  warning_days?: number;
  /** Days before deadline to trigger Red critical. Default 7. */
  critical_days?: number;
  /** Whether deadline-proximity degradation is applied. Default true. */
  deadline_degradation_enabled?: boolean;
}

/* ------------------------------------------------------------------------- */
/*  Scoring                                                                  */
/* ------------------------------------------------------------------------- */

function scoreBinary(
  config: BinaryConfig,
  data: MissionDataPoint[],
  asOfDate: string,
): SubKpiScore {
  const pts = data.map((d) => ({ date: d.date, value: d.value }));
  const cur = latestAsOf(pts, asOfDate);

  if (!cur) {
    return {
      score_0_100: null,
      // DEV-864: Binary uses an achievement badge, not a progress bar.
      progress_0_100: null,
      current_value: null,
      target_value: 1,
      baseline_value: 0,
      explain: 'No achievement data available as of this date.',
    };
  }

  const achieved = cur.value >= 1;

  if (achieved) {
    return {
      score_0_100: KPI_DEFAULTS.mission.binary_score_levels.achieved,
      // DEV-864: Binary uses badge, not bar — null progress.
      progress_0_100: null,
      current_value: cur.value,
      target_value: 1,
      baseline_value: 0,
      explain: 'Achievement completed.',
    };
  }

  // Not achieved — apply deadline degradation if configured.
  // Base score for "not achieved with no deadline" is 0 (binary fail).
  // Base score for "not achieved with deadline" is 100 (will be degraded
  // by the utility based on proximity), matching existing mission.ts behavior.
  const hasDeadline = !!config.deadline_date;
  const enabled = config.deadline_degradation_enabled ?? true;

  if (!hasDeadline || !enabled) {
    return {
      score_0_100: KPI_DEFAULTS.mission.binary_score_levels.failed,
      // DEV-864: Binary uses badge, not bar — null progress.
      progress_0_100: null,
      current_value: cur.value,
      target_value: 1,
      baseline_value: 0,
      explain: 'Not yet achieved.',
    };
  }

  // With deadline + enabled: the base score is 100 (on track), degraded
  // by deadline proximity. Goal is NOT met (we're in this branch because
  // achieved was false), so degradation applies.
  const degraded = applyDeadlineDegradation({
    base_score: KPI_DEFAULTS.mission.binary_score_levels.achieved,
    goal_met: false,
    asOfDate,
    config: {
      deadline_date: config.deadline_date,
      warning_days: config.warning_days,
      critical_days: config.critical_days,
      deadline_degradation_enabled: enabled,
    },
  });

  return {
    score_0_100: degraded.score,
    // DEV-864: Binary uses badge, not bar — null progress.
    progress_0_100: null,
    current_value: cur.value,
    target_value: 1,
    baseline_value: 0,
    explain: degraded.explain || `Not yet achieved. ${config.deadline_date ? `Deadline ${config.deadline_date}.` : ''}`.trim(),
  };
}

/* ------------------------------------------------------------------------- */
/*  Color derivation                                                         */
/* ------------------------------------------------------------------------- */

/**
 * Binary's color mapping is type-specific — it does NOT use universal
 * yellow/green score thresholds because Binary's score IS the tolerance
 * signal (100 = on track, 80 = warning, 70 = critical, 0 = failed).
 *
 * Mapping deliberately:
 *   100  → green   (achieved OR not yet achieved but deadline far away)
 *   80   → yellow  (warning window — deadline approaching without achievement)
 *   70   → red     (critical window — deadline imminent without achievement)
 *   0    → red     (overdue or not achieved with no deadline)
 *
 * Skip's principle (DEV-814): Binary's deadline-driven score values ARE its
 * tolerance signals. Adding separate yellow/green thresholds would create a
 * redundant field and a coloring conflict (the existing engine has this bug —
 * a 80-score Binary in warning window currently displays as green because
 * the universal green threshold is 80).
 *
 * Goes through the engine: this Binary-specific color mapping is the ONE
 * source of truth for Binary KPI coloring once the engine migrates to use
 * the new types architecture (DEV-814 Phase 4). Until then, the legacy
 * `mission.ts` path has its own equivalent fix in parallel.
 */
function colorFromScore(score: SubKpiScore): 0 | 1 | 2 | 3 {
  if (score.score_0_100 === null) return 0;
  const levels = KPI_DEFAULTS.mission.binary_score_levels;
  if (score.score_0_100 >= levels.achieved) return 3;       // green — on track or achieved
  if (score.score_0_100 >= levels.warning_window) return 2; // yellow — warning window
  return 1;                                                  // red — critical, overdue, or unachieved
}

/* ------------------------------------------------------------------------- */
/*  Validation                                                               */
/* ------------------------------------------------------------------------- */

function validateConfig(config: BinaryConfig): ValidationError | null {
  // DEV-816 / Skip: per-field required checks live in the schema and are
  // enforced by the wizard. Cross-field rules (warning_days strictly >
  // critical_days, etc.) are the only thing this validator owns.
  return validateCommonCrossFields(config);
}

/* ------------------------------------------------------------------------- */
/*  Schema, examples, and UI hints                                           */
/* ------------------------------------------------------------------------- */

const config_schema: ConfigFieldSchema[] = [
  {
    key: 'display_name',
    label: 'What outcome must be achieved?',
    type: 'string',
    required: true,
    help_text:
      'A short name for the achievement (e.g., "Legislation Passed", "School Operational").',
  },
  {
    key: 'achievement_definition',
    label: 'Definition (optional)',
    type: 'string',
    required: false,
    help_text: 'Describe what "achieved" means in concrete terms — what evidence confirms it?',
  },
  {
    key: 'tracking_start_date',
    label: 'Tracking start date (optional)',
    type: 'date',
    required: false,
    help_text: 'Scoring begins on this date. Reporting periods before this date are ignored.',
    inline_with_next: true, // pair with deadline_date (DEV-816)
  },
  {
    key: 'deadline_date',
    label: 'Target deadline',
    type: 'date',
    required: false,
    help_text:
      'When this achievement is expected. If set, the score may degrade as the deadline approaches.',
  },
  // DEV-816 / Skip's bug report: deadline_degradation_enabled was declared
  // as type: 'boolean' but ConfigFieldRenderer never had a boolean handler,
  // so the field rendered as a label + help text with no actual input — it
  // was invisible UI from day one. Removing the field entirely:
  //   • No user could change it through the UI, so nothing real is lost.
  //   • The "track deadline but don't degrade" niche use case is achievable
  //     by simply not setting a deadline.
  //   • Engine reads `config.deadline_degradation_enabled ?? true`, so
  //     existing saved Binary instances default to the prior behavior with
  //     no migration needed.
  // BinaryConfig.deadline_degradation_enabled remains optional in the type
  // so old persisted values don't error if loaded.
  {
    key: 'warning_days',
    label: 'Warning window (days before deadline)',
    type: 'number',
    required: false,
    default_value: 30,
    suffix: 'days',
    help_text: 'Within this many days of the deadline, the score drops to 80 (yellow). Default 30.',
    validation: { min: 0 },
    inline_with_next: true, // pair with critical_days (DEV-816)
  },
  {
    key: 'critical_days',
    label: 'Critical window (days before deadline)',
    type: 'number',
    required: false,
    default_value: 7,
    suffix: 'days',
    help_text: 'Within this many days of the deadline, the score drops to 70 (red-ish). Default 7.',
    validation: { min: 0 },
  },
];

const setup_examples: SetupExample[] = [
  {
    title: 'Legislation passed',
    description:
      'Track whether a specific bill is passed by a legislative body by a target date. ' +
      'Score degrades as the legislative session deadline approaches without passage.',
    example_config: {
      display_name: 'State Wastewater Bill Passed',
      achievement_definition: 'Bill signed into law by the governor.',
      deadline_date: '2027-06-30',
    },
  },
  {
    title: 'Certification obtained',
    description:
      'Track whether a regulatory or accreditation body issues a required certification. ' +
      'Score degrades as the project end-date deadline approaches without certification.',
    example_config: {
      display_name: 'Facility Operational Certification',
      achievement_definition: 'State environmental agency issues final operating permit.',
      deadline_date: '2026-12-31',
      warning_days: 60,
      critical_days: 14,
    },
  },
  {
    // DEV-816: previously demonstrated `deadline_degradation_enabled: false`.
    // That field is gone — equivalent behavior is now achieved by simply
    // not setting a deadline. The MOU example is reframed to teach the
    // new pattern.
    title: 'MOU signed (no urgency penalty)',
    description:
      'Track whether a partner organization signs an MOU when there is no hard ' +
      'deadline. Omit the deadline so the score stays at 0 until achieved (then 100), ' +
      'without a graduated urgency penalty.',
    example_config: {
      display_name: 'Rotary International MOU',
      achievement_definition: 'Both parties have signed and exchanged the MOU.',
    },
  },
];

const ui_hints: UiHints = {
  default_chart: 'gauge',
  value_format: 'count_with_unit',
  show_target_line: false,
};

/* ------------------------------------------------------------------------- */
/*  Export                                                                   */
/* ------------------------------------------------------------------------- */

export const binaryType: MissionSubKpiType<BinaryConfig> = {
  type_key: 'binary',
  display_name: 'Binary',
  description:
    'Track a single yes/no outcome that must be achieved by a deadline.',
  examples: [
    'contract signed',
    'permit obtained',
    'audit passed',
  ],
  use_when: 'The question is binary and the deadline matters.',
  supported_color_modes: ['deadline'],
  goal_direction: 'binary',
  config_schema,
  validate_config: validateConfig,
  score: scoreBinary,
  color_from_score: colorFromScore,
  ui_hints,
  setup_examples,
};
